import BackButton from "components/onboarding/create/BackButton";
import ButtonWrapper from "components/offerings/create/ButtonWrapper";
import InputGroup from "components/offerings/create/InputGroup";
import NextButton from "components/onboarding/create/NextButton";
import cls from "classnames";
import { Transition } from "@headlessui/react";
import { useEffect, useState } from "react";

export function renderField(field, parentProps) {
  const {
    component: _FieldComponent,
    disabled,
    visible,
    validator,
    formatValue,
    required,
    ...others
  } = field;

  const { formData, formErrors, onFieldChange, ...otherParentProps } =
    parentProps;

  return (
    <_FieldComponent
      onChange={(e) => onFieldChange(e, { formatValue, validator, required })}
      {...others}
      {...otherParentProps}
      formData={formData}
      value={formData ? formData[others.name] : null}
      error={formErrors ? formErrors[others.name] : null}
      disabled={typeof disabled === "function" ? disabled(formData) : disabled}
      visible={typeof visible === "function" ? visible(formData) : visible}
    />
  );
}

export default function FormStep({
  step,
  title,
  description,
  children,
  sideTitle,
  sideDescription,
  sideImage,
}) {
  return (
    <div className="flex flex-row flex-1 w-full overflow-auto">
      <MainContent
        step={step}
        title={title}
        description={description}
        children={children}
      />
      <SideContent
        className="md:block hidden"
        step={step}
        sideTitle={sideTitle}
        sideDescription={sideDescription}
        sideImage={sideImage}
      />
    </div>
  );
}

function MainContent({ step, title, description, children, className }) {
  return (
    <div className="flex flex-col items-center flex-1 md:shrink-0 overflow-auto">
      <div
        className={cls(
          "my-auto w-full max-w-[420px] flex md:items-center items-start justify-center",
          className
        )}
      >
        <div className="flex flex-col items-center h-auto md:w-fit w-full overflow-hidden">
          <div className="absolute top-[80px] left-0 md:hidden w-full h-2.5">
            <div
              className={cls(
                "bg-blue-600 h-2.5 rounded-r-full transition-width",
                { "w-[25%]": step === 0 },
                { "w-[50%]": step === 1 },
                { "w-[75%]": step === 2 }
              )}
              // style={{
              //   width: `${(step + 1) * 20}%`,
              // }}
            ></div>
          </div>
          <div
            className={cls(
              "flex flex-col flex-1 h-auto w-full sm:w-[420px] md:p-5 px-5 py-16 mb-24",
              step === 2 ? "gap-2" : "gap-9"
            )}
          >
            <div className="flex flex-col gap-2 h-auto w-full">
              <span className="text-[#16191B4D] font-bold text-sm w-full md:block hidden">
                {`STEP ${step + 1} OF 3`}
              </span>
              <h3 className="text-2xl leading-9 md:text-[32px] md:leading-[48px] font-extrabold">
                {title}
              </h3>
              {description && (
                <h3 className="text-sm md:text-base font-normal">
                  {description}
                </h3>
              )}
            </div>
            <div className="flex flex-col gap-3 h-auto w-full">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SideContent({
  step,
  sideTitle,
  sideDescription,
  sideImage,
  className,
}) {
  const [isShowing, setIsShowing] = useState(false);

  useEffect(() => {
    if (!isShowing) {
      setIsShowing(true);
    }
  }, [isShowing]);

  return (
    <div
      className={cls(
        "text-medium text-base flex-1 min-w-0 bg-[#F9FAFA] pt-8",
        className
      )}
      style={{
        background:
          "linear-gradient(149.52deg, #5060F7 5.74%, #030629 91.81%) ",
      }}
    >
      <Transition
        appear={true}
        show={isShowing}
        enter="transform transition duration-[240ms]"
        enterFrom="opacity-0 scale-75"
        enterTo="opacity-100 scale-100"
        className={
          "p-5 flex flex-col items-center justify-center gap-12 h-full"
        }
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <img
            className={cls(step === 2 ? "w-[60px]" : "w-[420px] max-h-[240px]")}
            src={sideImage}
            alt={sideTitle}
          />
        </div>
        {step === 2 && (
          <div className="flex flex-col items-center justify-center gap-4">
            <DescriptionCard
              className={"scale-75"}
              sideTitle={sideTitle[0]}
              sideDescription={sideDescription[0]}
            />
            <DescriptionCard
              sideTitle={sideTitle[1]}
              sideDescription={sideDescription[1]}
            />
            <DescriptionCard
              className={"scale-75"}
              sideTitle={sideTitle[2]}
              sideDescription={sideDescription[2]}
            />
          </div>
        )}
        {step !== 2 && (
          <DescriptionCard
            sideTitle={sideTitle}
            sideDescription={sideDescription}
          />
        )}
      </Transition>
    </div>
  );
}

function DescriptionCard({ className, sideTitle, sideDescription }) {
  return (
    <div
      className={cls(
        "flex flex-col items-center justify-center bg-[#FFFFFF] rounded-lg p-6 w-[420px]",
        className
      )}
    >
      <span className="text-[#16191B] text-2xl leading-9 w-full font-bold">
        {sideTitle}
      </span>
      <span className="text-[#69747B] text-base w-full font-normal">
        {sideDescription}
      </span>
    </div>
  );
}

export function FormFields({ fields, ...otherParentProps }) {
  return (
    <div className="flex flex-col gap-2 h-auto w-full">
      {fields.map((field, idx) => {
        let component;
        const { label, fields: subFields } = field;

        if (subFields) {
          component = (
            <InputGroup
              label={label}
              fields={subFields}
              {...otherParentProps}
            />
          );
        } else {
          component = renderField(field, otherParentProps);
        }

        return (
          <div className="mb-4" key={idx}>
            {component}
          </div>
        );
      })}
    </div>
  );
}

export function FormStepControls({ step, isLoading, goPrev, goNext }) {
  return (
    <ButtonWrapper step={step}>
      {step > 0 && (
        <BackButton handleBackButton={goPrev} disabled={isLoading} />
      )}
      <NextButton isLoading={isLoading} step={step} handleNextButton={goNext} />
    </ButtonWrapper>
  );
}
